/**
 * Socket.IO event contracts for the realtime dashboard channel.
 *
 * The backend emits these into a room-per-org (`org:<orgId>`); the dashboard
 * (via client-core) subscribes. Event NAMES are centralized so server + client
 * never drift, and each payload has a zod schema for runtime validation.
 */
import type { z } from 'zod';
import { object, string } from 'zod';

import { TicketSchema } from './feedback.js';
import { CommentSchema } from './comments.js';

/** Canonical Socket.IO event names (server → client). */
export const RealtimeEvent = {
  FeedbackCreated: 'feedback.created',
  FeedbackUpdated: 'feedback.updated',
  CommentCreated: 'comment.created',
} as const;
export type RealtimeEventName = (typeof RealtimeEvent)[keyof typeof RealtimeEvent];

/** Emitted when a new feedback ticket is ingested for an org. */
export const FeedbackCreatedPayloadSchema = object({
  organizationId: string().min(1),
  ticket: TicketSchema,
});
export type FeedbackCreatedPayload = z.infer<typeof FeedbackCreatedPayloadSchema>;

/**
 * Emitted when a ticket changes (status, AI enrichment finalized, clustered…).
 * Carries the full updated ticket so clients can replace in place.
 */
export const FeedbackUpdatedPayloadSchema = object({
  organizationId: string().min(1),
  ticket: TicketSchema,
});
export type FeedbackUpdatedPayload = z.infer<typeof FeedbackUpdatedPayloadSchema>;

/** Emitted when an internal comment is added to a ticket. */
export const CommentCreatedPayloadSchema = object({
  organizationId: string().min(1),
  sessionId: string().min(1),
  comment: CommentSchema,
});
export type CommentCreatedPayload = z.infer<typeof CommentCreatedPayloadSchema>;

/**
 * Typed Socket.IO server→client event map. Use with
 * `Server<ClientToServerEvents, ServerToClientEvents>` /
 * `Socket<ServerToClientEvents>` on both ends for end-to-end typing.
 */
export interface ServerToClientEvents {
  'feedback.created': (payload: FeedbackCreatedPayload) => void;
  'feedback.updated': (payload: FeedbackUpdatedPayload) => void;
  'comment.created': (payload: CommentCreatedPayload) => void;
}

/** Client→server events on the dashboard channel (currently none beyond connect). */
export interface ClientToServerEvents {
  /** Reserved for explicit room (re)subscription if needed later. */
  subscribe?: (payload: { organizationId: string }) => void;
}
